import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { X, Plus } from '@phosphor-icons/react';

// Coded values are stored as { code, label }. Entries without a published code
// keep code = null until the official ECCAIRS taxonomy pack is installed.
export function findEntryIndex(entries, value) {
  if (!value || !entries) return -1;
  if (value.code != null) {
    const byCode = entries.findIndex((e) => e.code != null && String(e.code) === String(value.code));
    if (byCode >= 0) return byCode;
  }
  const label = (value.label || '').toLowerCase();
  return entries.findIndex((e) => e.label.toLowerCase() === label);
}

const toValue = (entry) => (entry ? { code: entry.code ?? null, label: entry.label } : null);

export function CodedSelect({ entries = [], value, onChange, disabled, placeholder = 'Select', testId }) {
  const index = findEntryIndex(entries, value);
  return (
    <Select
      value={index >= 0 ? String(index) : ''}
      onValueChange={(v) => onChange(v === '__clear' ? null : toValue(entries[Number(v)]))}
      disabled={disabled}
    >
      <SelectTrigger className="rounded-lg" data-testid={testId}>
        <SelectValue placeholder={value && index < 0 ? value.label : placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        <SelectItem value="__clear"><span className="text-slate-400">Not set</span></SelectItem>
        {entries.map((entry, i) => (
          <SelectItem key={`${entry.code ?? 'x'}-${i}`} value={String(i)}>
            <span className={entry.level === 1 ? 'font-semibold' : ''}>{entry.label}</span>
            {entry.code != null && <span className="ml-2 text-xs text-slate-400 font-mono">{entry.code}</span>}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function MultiCodedSelect({ entries, value = [], onChange, disabled, testId }) {
  const remaining = entries.filter((e) => findEntryIndex(value, e) < 0);
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex flex-wrap gap-2">
        {value.length === 0 && <span className="text-sm text-slate-400">None selected</span>}
        {value.map((v, i) => (
          <span key={`${v.code}-${i}`} className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 border border-blue-200 rounded-full px-3 py-1 text-xs font-medium">
            {v.label}
            {!disabled && (
              <button type="button" aria-label={`Remove ${v.label}`} onClick={() => onChange(value.filter((_, j) => j !== i))}>
                <X size={12} />
              </button>
            )}
          </span>
        ))}
      </div>
      {!disabled && remaining.length > 0 && (
        <CodedSelect entries={remaining} value={null} onChange={(v) => v && onChange([...value, v])} placeholder="Add a category" />
      )}
    </div>
  );
}

export function FieldInput({ field, value, onChange, valueLists, disabled, missing }) {
  const entries = valueLists?.[field.value_list] || [];
  const id = `f-${field.key}`;
  let control;
  if (field.type === 'select') {
    control = <CodedSelect entries={entries} value={value} onChange={onChange} disabled={disabled} testId={id} />;
  } else if (field.type === 'multiselect') {
    control = <MultiCodedSelect entries={entries} value={value || []} onChange={onChange} disabled={disabled} testId={id} />;
  } else if (field.type === 'textarea') {
    control = <Textarea id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)} disabled={disabled} rows={3} className="rounded-lg" />;
  } else {
    const type = { date: 'date', time: 'time', number: 'number' }[field.type] || 'text';
    control = <Input id={id} type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="rounded-lg" />;
  }
  return (
    <div className={field.type === 'textarea' || field.type === 'multiselect' ? 'md:col-span-2' : ''}>
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <Label htmlFor={id} className={missing ? 'text-red-700' : ''}>
          {field.label}
          {field.required?.length > 0 && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <span className="text-[10px] text-slate-400 font-mono whitespace-nowrap">
          {field.annex13 && `A13 4.2${field.annex13} `}{field.attr && `#${field.attr}`}
        </span>
      </div>
      {control}
      {field.help && <p className="text-xs text-slate-500 mt-1">{field.help}</p>}
    </div>
  );
}

export function FieldGrid({ fields, values, onChange, valueLists, disabled, missingKeys }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
      {fields.map((field) => (
        <FieldInput
          key={field.key}
          field={field}
          value={values?.[field.key]}
          onChange={(v) => onChange({ ...values, [field.key]: v })}
          valueLists={valueLists}
          disabled={disabled}
          missing={missingKeys?.has(field.key)}
        />
      ))}
    </div>
  );
}

export function InjuryTable({ rows, columns, value = {}, onChange, disabled }) {
  const set = (row, col, raw) => {
    const next = { ...value, [row]: { ...(value[row] || {}) } };
    if (raw === '') delete next[row][col];
    else next[row][col] = Math.max(0, parseInt(raw, 10) || 0);
    onChange(next);
  };
  const total = (col) => rows.reduce((sum, r) => sum + (Number(value[r.key]?.[col]) || 0), 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border border-slate-200 rounded-lg" data-testid="injury-table">
        <thead className="bg-slate-50">
          <tr>
            <th className="text-left px-3 py-2 font-medium text-slate-600">Persons</th>
            {columns.map((c) => <th key={c.key} className="px-3 py-2 font-medium text-slate-600 w-24">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-slate-100">
              <td className="px-3 py-1.5 text-slate-700">{r.label}</td>
              {columns.map((c) => (
                <td key={c.key} className="px-2 py-1">
                  <Input type="number" min="0" aria-label={`${r.label} ${c.label}`} value={value[r.key]?.[c.key] ?? ''}
                    onChange={(e) => set(r.key, c.key, e.target.value)} disabled={disabled} className="h-8 rounded-md text-center" />
                </td>
              ))}
            </tr>
          ))}
          <tr className="border-t border-slate-200 bg-slate-50 font-medium">
            <td className="px-3 py-2 text-slate-700">Total</td>
            {columns.map((c) => <td key={c.key} className="px-3 py-2 text-center text-slate-900">{total(c.key)}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function AddButton({ onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-800 border border-dashed border-blue-300 rounded-lg px-3 py-2 hover:bg-blue-50">
      <Plus size={14} /> {children}
    </button>
  );
}
